import {WorkspacesDataSource} from "./WorkspacesDataSource";
import {ItemsDataSource} from "./ItemsDataSource";
import {QuizzesDataSource} from "./QuizzesDataSource";
import {TaskListUnitDS} from "../taskLists/TaskListUnitDS";
import {IssueDS} from "../reliability/IssueDS";
import {LocationDS} from "../reliability/LocationDS";
import {AuditTemplateDS} from "../audit/AuditTemplateDS";
import {AuditAssignmentDS} from "../audit/AuditAssignmentDS";
import {ClientActiveRecord} from "./ClientActiveRecord";
import {WsUsersDataSource} from "./WsUsersDataSource";
import {UsersDataSource} from "./UsersDataSource";
import {UserWorkspacesDataSource} from "./UserWorkspacesDataSource";
import {NotificationsDataSource} from "./NotificationsDataSource";
import {ClientUsageTracker} from "../ClientUsageTracker";
import {AccessRolesDataSource} from "./AccessRolesDataSource";
import {UserRolesDataSource} from "./UserRolesDataSource";
import {RolePermissionsDataSource} from "./RolePermissionsDataSource";

import {Workspace} from "../../API";
import {IDataClientService} from "../../platform/services/data/DataClientService";
import {IAuthService} from "../../platform/services/auth/AuthService";
import {AppState} from "../AppState";


type WorkspaceId = string;


export class DataSourceManager {
    public dataClientService: IDataClientService;
    private authService: IAuthService;
    private appState: AppState;

    workspacesDS: WorkspacesDataSource;
    notificationsDS: NotificationsDataSource;
    userWorkspacesDS: UserWorkspacesDataSource;
    usersDS: UsersDataSource;
    clientAR: ClientActiveRecord;
    private clientUsageTracker?: ClientUsageTracker;
    accessRolesDS?: AccessRolesDataSource;
    userRolesDS?: UserRolesDataSource;
    rolePermissionsDS?: RolePermissionsDataSource;

    itemsByWs: Map<WorkspaceId, ItemsDataSource> = new Map();
    quizzesByWs: Map<WorkspaceId, QuizzesDataSource> = new Map();
    usersByWs: Map<WorkspaceId, WsUsersDataSource> = new Map();
    taskListUnitsByWs: Map<WorkspaceId, TaskListUnitDS> = new Map();
    issuesByWs: Map<WorkspaceId, IssueDS> = new Map();
    locationsByWs: Map<WorkspaceId, LocationDS> = new Map();
    auditsByWs: Map<WorkspaceId, AuditTemplateDS> = new Map();
    auditAssignmentsByWs: Map<WorkspaceId, AuditAssignmentDS> = new Map();

    private tenantId: string;
    private currentUserId: string;

    constructor(
        dataClientService: IDataClientService,
        authService: IAuthService,
        appState: AppState,
        tenantId: string,
        currentUserId: string
    ) {
        this.appState = appState;
        this.dataClientService = dataClientService;
        this.authService = authService;
        this.tenantId = tenantId;
        this.currentUserId = currentUserId;

        // Create DataSources with proper dependencies
        this.workspacesDS = new WorkspacesDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
            rootStore: this.appState.rootStore,
        });

        this.notificationsDS = new NotificationsDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
        });

        this.userWorkspacesDS = new UserWorkspacesDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
        }, currentUserId);

        this.usersDS = new UsersDataSource({
            dataClientService,
            authService,
            userState: this.appState.userState!,
        });

        this.clientAR = new ClientActiveRecord({
                dataClientService,
                authService,
            },
            tenantId
        );

        this.clientUsageTracker = new ClientUsageTracker(
            dataClientService,
            authService,
            this.clientAR,
            tenantId
        );
    }

    async init() {
        await this.workspacesDS.initialLoad();
        await this.notificationsDS.initialLoad(false);
        await this.clientAR.initialLoad();
        await this.clientUsageTracker?.init();
        await this.userWorkspacesDS.initialLoad();
    }

    getWorkspaceById(wsId: string): Workspace | undefined {
        return this.workspacesDS.allRows.find(wsDB => wsDB.id === wsId);
    }

    destroy() {
        this.workspacesDS.destroy();
        this.notificationsDS.destroy();
        this.userWorkspacesDS.destroy();
        this.usersDS.destroy();
        this.clientAR.destroy();
        this.clientUsageTracker?.destroy();
        this.accessRolesDS?.destroy();
        this.userRolesDS?.destroy();
        this.rolePermissionsDS?.destroy();

        Array.from(this.itemsByWs.values()).map(dataSource => dataSource.destroy());
        Array.from(this.quizzesByWs.values()).map(dataSource => dataSource.destroy());
        Array.from(this.taskListUnitsByWs.values()).map(dataSource => dataSource.destroy());
        Array.from(this.issuesByWs.values()).map(store => store.destroy());
        Array.from(this.locationsByWs.values()).map(store => store.destroy());
        Array.from(this.auditsByWs.values()).map(ds => ds.destroy());
        Array.from(this.auditAssignmentsByWs.values()).map(ds => ds.destroy());
    }

    getItemsDataSource(wsId: WorkspaceId) {
        if (!this.itemsByWs.has(wsId)) {
            this.itemsByWs.set(wsId, new ItemsDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
                userState: this.appState.userState!,
                rootStore: this.appState.rootStore,
            }, wsId));
        }
        return this.itemsByWs.get(wsId)!;
    }

    getQuizzesDataSource(wsId: WorkspaceId) {
        if (!this.quizzesByWs.has(wsId)) {
            this.quizzesByWs.set(wsId, new QuizzesDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
                userState: this.appState.userState!,
                appState: this.appState
            }, wsId));
        }
        return this.quizzesByWs.get(wsId)!;
    }

    getWsUsersDataSource(wsId: WorkspaceId) {
        if (!this.usersByWs.has(wsId)) {
            this.usersByWs.set(wsId, new WsUsersDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
                userState: this.appState.userState!,
                rootStore: this.appState.rootStore,
            }, wsId));
        }
        return this.usersByWs.get(wsId)!;
    }

    getTaskListUnitsDataSource(wsId: WorkspaceId) {
        if (!this.taskListUnitsByWs.has(wsId)) {
            this.taskListUnitsByWs.set(wsId, new TaskListUnitDS({
                dataClientService: this.dataClientService,
                authService: this.authService
            }, wsId));
        }
        return this.taskListUnitsByWs.get(wsId)!;
    }

    getIssueDS(wsId: WorkspaceId): IssueDS {
        if (!this.issuesByWs.has(wsId)) {
            this.issuesByWs.set(wsId, new IssueDS(
                {dataClientService: this.dataClientService, authService: this.authService},
                wsId,
            ));
        }
        return this.issuesByWs.get(wsId)!;
    }

    getLocationDS(wsId: WorkspaceId): LocationDS {
        if (!this.locationsByWs.has(wsId)) {
            this.locationsByWs.set(wsId, new LocationDS(
                {dataClientService: this.dataClientService, authService: this.authService},
                wsId,
            ));
        }
        return this.locationsByWs.get(wsId)!;
    }

    getAuditsDataSource(wsId: WorkspaceId): AuditTemplateDS {
        if (!this.auditsByWs.has(wsId)) {
            this.auditsByWs.set(wsId, new AuditTemplateDS({
                dataClientService: this.dataClientService,
                authService: this.authService,
            }, wsId));
        }
        return this.auditsByWs.get(wsId)!;
    }

    getAuditAssignmentDS(wsId: WorkspaceId): AuditAssignmentDS {
        if (!this.auditAssignmentsByWs.has(wsId)) {
            this.auditAssignmentsByWs.set(wsId, new AuditAssignmentDS({
                dataClientService: this.dataClientService,
                authService: this.authService,
            }, wsId));
        }
        return this.auditAssignmentsByWs.get(wsId)!;
    }

    getDataClientService(): IDataClientService {
        return this.dataClientService;
    }

    getAccessRolesDataSource(): AccessRolesDataSource {
        if (!this.accessRolesDS) {
            this.accessRolesDS = new AccessRolesDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
            });
        }
        return this.accessRolesDS;
    }

    getUserRolesDataSource(): UserRolesDataSource {
        if (!this.userRolesDS) {
            this.userRolesDS = new UserRolesDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
            });
        }
        return this.userRolesDS;
    }

    getRolePermissionsDataSource(): RolePermissionsDataSource {
        if (!this.rolePermissionsDS) {
            this.rolePermissionsDS = new RolePermissionsDataSource({
                dataClientService: this.dataClientService,
                authService: this.authService,
            });
        }
        return this.rolePermissionsDS;
    }
}
